import type { PlanSubmissionKind } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import {
  type MissedDeadlinePlanNotice,
  PlanAutoSubmitNoticeReason,
  type UnsubmittedPlanNotice,
} from '@ebalance/proto/bffuser/v1/plan_auto_submit_notice_model_pb'
import type { TFunction } from 'i18next'

import { groupContiguousKomaRanges } from '~/models/contiguousKomaRanges'
import { formatKomaLabel, formatKomaRangeLabel } from '~/models/komaLabel'

/**
 * 自動で提出・出し直しできなかった計画の知らせ (BFF `GlobalAlertService.ListPlanAutoSubmitNotices`)。
 * 2 つの一覧は独立していて、同じ計画が両方に出ることがある。並びは server が決めた順
 * (対象日 → 計画断面 → エリア → 計画提出者コード) のまま使い、ここでは並べ替えない。
 */
export type PlanAutoSubmitNotices = {
  /** 自動で提出できていない計画 (AC-WFT-045 / AC-PAR-004 / AC-PAR-006)。計画ごとに 1 行。 */
  unsubmitted: readonly UnsubmittedPlanNotice[]
  /** 締切までに出し直せなかった計画 (AC-PAR-009)。計画ごとに 1 行。 */
  missedDeadline: readonly MissedDeadlinePlanNotice[]
}

/** 取得中・取得失敗・知らせなしの値。 */
export const EMPTY_PLAN_AUTO_SUBMIT_NOTICES: PlanAutoSubmitNotices = {
  unsubmitted: [],
  missedDeadline: [],
}

/** 一覧のうち、その計画種別の画面に出す行 (server の順のまま)。 */
export const planAutoSubmitNoticesOfKind = (
  notices: PlanAutoSubmitNotices,
  planKind: PlanSubmissionKind,
): PlanAutoSubmitNotices => ({
  unsubmitted: notices.unsubmitted.filter((n) => n.plan?.kind === planKind),
  missedDeadline: notices.missedDeadline.filter(
    (n) => n.plan?.kind === planKind,
  ),
})

/**
 * 「自動で提出できていない計画」のバナーの本文「理由を確認し、必要な計画は提出してください。」を出すか。
 * 本文の対象かは backend が `asks_to_submit` で決める (中断の計画は対象にしない。AC-PLC-082 ※)。
 * 対象の行が 1 つも無ければ本文を出さない (#12699 で仮採用。中断の計画しか無い時に「提出してください」と出さない)。
 */
export const asksToSubmitAny = (
  rows: readonly UnsubmittedPlanNotice[],
): boolean => rows.some((n) => n.asksToSubmit)

/**
 * 理由 → `common:planAutoSubmitNotice.reason.*` の key (文言は AC-PLC-082 の一覧の理由)。
 * UNSPECIFIED (BFF が未知の値を落とした時) は理由なし (`undefined`)。
 */
const REASON_KEYS: Record<PlanAutoSubmitNoticeReason, string | undefined> = {
  [PlanAutoSubmitNoticeReason.UNSPECIFIED]: undefined,
  [PlanAutoSubmitNoticeReason.UNRESOLVED_INCONSISTENCY]:
    'unresolvedInconsistency',
  [PlanAutoSubmitNoticeReason.SUPERSEDED]: 'superseded',
  [PlanAutoSubmitNoticeReason.EMPTY_SLOTS]: 'emptySlots',
  [PlanAutoSubmitNoticeReason.FIRST_SUBMISSION]: 'firstSubmission',
  [PlanAutoSubmitNoticeReason.DEMAND_FORECAST_UNCONFIRMED]:
    'demandForecastUnconfirmed',
  [PlanAutoSubmitNoticeReason.MISSING_COUNTERPARTY_ROWS]:
    'missingCounterpartyRows',
  [PlanAutoSubmitNoticeReason.HELD_PAST_DEADLINE]: 'heldPastDeadline',
  [PlanAutoSubmitNoticeReason.SUCCESSOR_NOT_ACCEPTED]: 'successorNotAccepted',
  [PlanAutoSubmitNoticeReason.SUCCESSOR_IN_PROGRESS]: 'successorInProgress',
  [PlanAutoSubmitNoticeReason.SUCCESSOR_GENERATION_FAILED]:
    'successorGenerationFailed',
  [PlanAutoSubmitNoticeReason.WORKFLOW_SUSPENDED]: 'workflowSuspended',
}

/** 理由の表示文言。UNSPECIFIED は `undefined` (呼び出し側で空欄の表示にする)。 */
export const planAutoSubmitNoticeReasonText = (
  t: TFunction,
  reason: PlanAutoSubmitNoticeReason,
): string | undefined => {
  const key = REASON_KEYS[reason]
  return key === undefined
    ? undefined
    : t(`common:planAutoSubmitNotice.reason.${key}`)
}

/**
 * 出し直せなかったコマの表示 (AC-PLC-082。並べ方は design `_navFormatKomaChunks`)。
 * 連続するコマは範囲にまとめ (「コマ 13〜16 (06:00〜08:00)」)、塊は「、」 / ", " でつなぐ。
 * コマは backend が昇順・重複なしで返す。
 */
export const formatMissedKomas = (
  t: TFunction,
  komas: readonly number[],
): string =>
  groupContiguousKomaRanges(komas)
    .map((range) =>
      range.start === range.end
        ? formatKomaLabel(t, range.start)
        : formatKomaRangeLabel(t, range.start, range.end),
    )
    .join(t('common:planAutoSubmitNotice.komaSeparator'))
